/**
 * Folding back-to-back actions in the Build transcript (eddy, 2026-10-26).
 *
 * A build often takes a dozen steps in a row — click, type, screenshot,
 * click — and one row per step buries EDDY's own words between them. Two or
 * more consecutive activity items become one run: the transcript shows only
 * the newest, plus a toggle that unfolds the earlier ones in place.
 *
 * A run is keyed by its FIRST item's seq, so the key stays put while the run
 * grows and an unfolded run stays unfolded as new steps land.
 */

import type { TranscriptItem } from './coding-events';

export type ActivityItem = Extract<TranscriptItem, { kind: 'activity' }>;

export type GroupedTranscriptItem =
  | Exclude<TranscriptItem, { kind: 'activity' }>
  | ActivityItem
  | { kind: 'activity-run'; seq: number; items: ActivityItem[] };

export function groupActivityRuns(items: TranscriptItem[]): GroupedTranscriptItem[] {
  const out: GroupedTranscriptItem[] = [];
  let run: ActivityItem[] = [];
  const flush = () => {
    if (run.length === 1) out.push(run[0]);
    else if (run.length > 1) out.push({ kind: 'activity-run', seq: run[0].seq, items: run });
    run = [];
  };
  for (const item of items) {
    if (item.kind === 'activity') {
      run.push(item);
      continue;
    }
    flush();
    out.push(item);
  }
  flush();
  return out;
}

/** The toggle's words for a folded run: how many steps sit above the newest, and how many of those failed. */
export function earlierActionsLabel(run: ActivityItem[]): string {
  const earlier = run.slice(0, -1);
  const failed = earlier.filter((item) => item.result === 'error').length;
  const count = `${earlier.length} earlier ${earlier.length === 1 ? 'action' : 'actions'}`;
  return failed > 0 ? `${count} · ${failed} failed` : count;
}
